import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Chip,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BuildIcon from '@mui/icons-material/Build';
import InventoryIcon from '@mui/icons-material/Inventory';
import NotesIcon from '@mui/icons-material/Notes';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { format } from 'date-fns';

function CopyFromMaintenanceDialog({ open, handleClose, maintenance, setMaintenance, mode = 'copy', onPick }) {
  const { t } = useTranslation();
  const isPick = mode === 'pick';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('none');
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [copyParts, setCopyParts] = useState(true);
  const [copyJobs, setCopyJobs] = useState(true);
  const [copyNotes, setCopyNotes] = useState(false);
  const [copyDescription, setCopyDescription] = useState(false);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const autoCloseTimerRef = useRef(null);

  const vehicle = !isPick ? maintenance?.vehicle : null;
  const hasTecdoc = vehicle?.tecdoc_id != null;
  const hasMakeModel = vehicle?.manufacturer && vehicle?.model;
  const hasEngineCode = vehicle?.engine_code;

  const fetchMaintenances = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ordering: '-created_at',
        page_size: 20,
        include_counts: 'true',
      };

      if (!isPick && maintenance?.id) params.exclude_id = maintenance.id;

      if (search) params.search = search;

      if (filterMode === 'tecdoc' && hasTecdoc) {
        params.vehicle_tecdoc_id = vehicle.tecdoc_id;
      } else if (filterMode === 'make_model' && hasMakeModel) {
        params.vehicle_manufacturer = vehicle.manufacturer;
        params.vehicle_model = vehicle.model;
      } else if (filterMode === 'full_match' && hasMakeModel) {
        params.vehicle_manufacturer = vehicle.manufacturer;
        params.vehicle_model = vehicle.model;
        if (vehicle.automatic != null) params.vehicle_automatic = vehicle.automatic;
        if (vehicle.engine_code) params.vehicle_engine_code = vehicle.engine_code;
      }

      const response = await axios.get('/api/maintenances/', { params });
      const data = response.data.results || response.data;
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(t('copyFromMaintenance.error'));
    } finally {
      setLoading(false);
    }
  }, [search, filterMode, maintenance?.id, vehicle, hasTecdoc, hasMakeModel, isPick, t]);

  useEffect(() => {
    if (!open) return;
    setSelectedMaintenance(null);
    setError(null);
    setSuccess(null);
    setSearch('');
    setFilterMode('none');
    setCopyParts(true);
    setCopyJobs(true);
    setCopyNotes(false);
    setCopyDescription(false);
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => fetchMaintenances(), 300);
    return () => clearTimeout(timer);
  }, [open, fetchMaintenances]);

  const handleCopy = async () => {
    if (!selectedMaintenance) return;
    const checkedAny = isPick ? (copyParts || copyJobs) : (copyParts || copyJobs || copyNotes || copyDescription);
    if (!checkedAny) {
      setError(t('copyFromMaintenance.atLeastOne'));
      return;
    }
    setCopying(true);
    setError(null);
    try {
      if (isPick) {
        await onPick({ maintenance: selectedMaintenance, copyParts, copyJobs });
        setSuccess(t('copyFromMaintenance.success'));
        autoCloseTimerRef.current = setTimeout(() => handleClose(), 1200);
      } else {
        const response = await axios.post(`/api/maintenances/${maintenance.id}/copy-from/`, {
          source_maintenance_id: selectedMaintenance.id,
          copy_parts: copyParts,
          copy_jobs: copyJobs,
          copy_notes: copyNotes,
          copy_description: copyDescription,
        });
        setMaintenance((prev) => ({ ...prev, ...response.data }));
        setSuccess(t('copyFromMaintenance.success'));
        autoCloseTimerRef.current = setTimeout(() => handleClose(), 1200);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('copyFromMaintenance.error'));
    } finally {
      setCopying(false);
    }
  };

  const atLeastOneChecked = isPick ? (copyParts || copyJobs) : (copyParts || copyJobs || copyNotes || copyDescription);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContentCopyIcon sx={{ color: 'secondary.main' }} />
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t(isPick ? 'copyFromMaintenance.pickTitle' : 'copyFromMaintenance.title')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'text.disabled' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 1 }}>
        {success ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
            <Typography sx={{ fontWeight: 500 }}>{success}</Typography>
          </Box>
        ) : (
          <>
            {!isPick && (
              <ToggleButtonGroup
                value={filterMode}
                exclusive
                onChange={(e, val) => { if (val !== null) setFilterMode(val); }}
                size="small"
                sx={{
                  mb: 2,
                  mt: 1,
                  flexWrap: 'wrap',
                  gap: 0.5,
                  '& .MuiToggleButton-root': {
                    borderRadius: '8px !important',
                    border: '1px solid',
                    borderColor: 'divider',
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.5,
                    fontSize: '13px',
                    fontWeight: 500,
                    '&.Mui-selected': {
                      bgcolor: 'secondary.main',
                      color: 'white',
                      borderColor: 'secondary.main',
                      '&:hover': { bgcolor: 'secondary.dark' },
                    },
                  },
                }}
              >
                <ToggleButton value="none">
                  {t('copyFromMaintenance.filterFree')}
                </ToggleButton>
                <Tooltip title={!hasTecdoc ? t('copyFromMaintenance.noVehicleTecdoc') : ''}>
                  <span>
                    <ToggleButton value="tecdoc" disabled={!hasTecdoc}>
                      {t('copyFromMaintenance.filterTecdoc')}
                    </ToggleButton>
                  </span>
                </Tooltip>
                <ToggleButton value="make_model" disabled={!hasMakeModel}>
                  {t('copyFromMaintenance.filterMakeModel')}
                </ToggleButton>
                <Tooltip title={!hasEngineCode ? t('copyFromMaintenance.noVehicleEngineCode') : ''}>
                  <span>
                    <ToggleButton value="full_match" disabled={!hasMakeModel || !hasEngineCode}>
                      {t('copyFromMaintenance.filterFullMatch')}
                    </ToggleButton>
                  </span>
                </Tooltip>
              </ToggleButtonGroup>
            )}

            <TextField
              placeholder={t('copyFromMaintenance.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              size="small"
              autoFocus
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': { borderRadius: '10px' },
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : results.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {t('copyFromMaintenance.noResults')}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 350, overflow: 'auto' }}>
                {results.map((m) => (
                  <Box
                    key={m.id}
                    onClick={() => setSelectedMaintenance(m)}
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      border: '1px solid',
                      borderColor: selectedMaintenance?.id === m.id ? 'secondary.main' : 'divider',
                      bgcolor: selectedMaintenance?.id === m.id ? 'action.selected' : 'background.paper',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: 'secondary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                        #{m.work_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {m.created_at ? format(new Date(m.created_at), 'dd.MM.yyyy') : ''}
                      </Typography>
                    </Box>
                    {m.vehicle && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                        {m.vehicle.registration_number}
                        {m.vehicle.manufacturer && ` — ${m.vehicle.manufacturer}`}
                        {m.vehicle.model && ` ${m.vehicle.model}`}
                        {m.vehicle.year_model && ` (${m.vehicle.year_model})`}
                      </Typography>
                    )}
                    {m.customer?.name && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {m.customer.name}
                      </Typography>
                    )}
                    {m.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                        }}
                      >
                        {m.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {m.parts_count > 0 && (
                        <Chip
                          size="small"
                          icon={<InventoryIcon sx={{ fontSize: '14px !important' }} />}
                          label={t('copyFromMaintenance.partsCount', { count: m.parts_count })}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '12px' }}
                        />
                      )}
                      {m.jobs_count > 0 && (
                        <Chip
                          size="small"
                          icon={<BuildIcon sx={{ fontSize: '14px !important' }} />}
                          label={t('copyFromMaintenance.jobsCount', { count: m.jobs_count })}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '12px' }}
                        />
                      )}
                      {m.notes_count > 0 && (
                        <Chip
                          size="small"
                          icon={<NotesIcon sx={{ fontSize: '14px !important' }} />}
                          label={t('copyFromMaintenance.notesCount', { count: m.notes_count })}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '12px' }}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {selectedMaintenance && (
              <Box sx={{ mt: 2, p: 2, borderRadius: '10px', bgcolor: 'background.default' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '13px', mb: 1 }}>
                  {t('copyFromMaintenance.selectSource')}
                </Typography>
                <FormGroup row sx={{ flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={<Checkbox checked={copyParts} onChange={(e) => setCopyParts(e.target.checked)} size="small" />}
                    label={
                      <Typography variant="body2">
                        {t('copyFromMaintenance.copyParts')}
                        {selectedMaintenance.parts_count > 0 && ` (${selectedMaintenance.parts_count})`}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={<Checkbox checked={copyJobs} onChange={(e) => setCopyJobs(e.target.checked)} size="small" />}
                    label={
                      <Typography variant="body2">
                        {t('copyFromMaintenance.copyJobs')}
                        {selectedMaintenance.jobs_count > 0 && ` (${selectedMaintenance.jobs_count})`}
                      </Typography>
                    }
                  />
                  {!isPick && (
                    <FormControlLabel
                      control={<Checkbox checked={copyNotes} onChange={(e) => setCopyNotes(e.target.checked)} size="small" />}
                      label={
                        <Typography variant="body2">
                          {t('copyFromMaintenance.copyNotes')}
                          {selectedMaintenance.notes_count > 0 && ` (${selectedMaintenance.notes_count})`}
                        </Typography>
                      }
                    />
                  )}
                  {!isPick && (
                    <FormControlLabel
                      control={<Checkbox checked={copyDescription} onChange={(e) => setCopyDescription(e.target.checked)} size="small" />}
                      label={<Typography variant="body2">{t('copyFromMaintenance.copyDescription')}</Typography>}
                    />
                  )}
                </FormGroup>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              borderRadius: '10px',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              '&:hover': { borderColor: 'divider', bgcolor: 'background.default' },
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleCopy}
            variant="contained"
            disabled={!selectedMaintenance || !atLeastOneChecked || copying}
            sx={{
              borderRadius: '10px',
              bgcolor: 'secondary.main',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              '&:hover': { bgcolor: 'secondary.light' },
            }}
          >
            {copying ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('copyFromMaintenance.copyButton')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default CopyFromMaintenanceDialog;
